// Callstack plugin.
import runPipeline from '../sdp/pipeline'
import { getTrackDscpMapping } from '../utils/dscp'

// Other Plugins
import { logManager } from '../../logs'
import BasicError, { callCodes } from '../../errors'
import { getOptions, getCallByWebrtcSessionId } from '../../call/interfaceNew/selectors'

// WebRTC operations.
import * as mediaOps from './media'

// Libraries
import { call, all, select } from 'redux-saga/effects'
import { isEmpty, isUndefined } from 'lodash/fp'

const log = logManager.getLogger('CALLSTACK')

/**
 * Recreates the internal Peer of a Session.
 * @method recreatePeer
 * @param {Object} webRTC    The WebRTC stack.
 * @param {string} sessionId The Session to act on.
 */
export function * recreatePeer (webRTC, sessionId) {
  let session
  try {
    session = yield call([webRTC.sessionManager, 'get'], sessionId)
  } catch (err) {
    /*
     * A WebRTC operation should only throw in Proxy mode when the channel times-out
     *    the operation. Check the first "get" in the saga to catch this problem.
     */
    log.warn(err.message)
    return
  }
  if (!session) {
    log.debug(`webRTC session ${sessionId} not found.`)
    return
  }

  yield call([session, 'recreatePeer'])
}

/**
 * Performs the webRTC session functions associated with closing a call
 *
 * @method closeCall
 * @param {Object} webRTC A WebRTC instance
 * @param {string} sessionId the local webRTC session id, used to lookup the session object
 */
export function * closeCall (webRTC, sessionId) {
  let session
  try {
    session = yield call([webRTC.sessionManager, 'get'], sessionId)
  } catch (err) {
    /*
     * A WebRTC operation should only throw in Proxy mode when the channel times-out
     *    the operation. Check the first "get" in the saga to catch this problem.
     */
    log.warn('Failed to clean-up call resources.')
    return
  }
  if (!session) {
    log.debug(`webRTC session ${sessionId} not found.`)
    return
  }

  yield all(session.localTracks.map(track => call([track, 'stop'])))

  yield call([session, 'end'])
}

/**
 * Performs webRTC portions of receiving an "offer" Session Description Protocol
 *
 * @method handleOffer
 * @param  {Object} deps
 * @param  {Object} deps.webRTC      The WebRTC stack.
 * @param  {string} offer Session Description protocol offer
 * @param  {string} webrtcSessionId local webrtc session id
 * @param {BandwidthControls} bandwidth bandwidth configurations to use
 * @returns {Object}
 */
export function * handleOffer (deps, offer, webrtcSessionId, bandwidth) {
  const { webRTC } = deps
  let session
  try {
    session = yield call([webRTC.sessionManager, 'get'], webrtcSessionId)
  } catch (err) {
    /*
     * A WebRTC operation should only throw in Proxy mode when the channel times-out
     *    the operation. Check the first "get" in the saga to catch this problem.
     */
    log.info(err.message)
    return { error: err }
  }

  if (!session) {
    log.debug(`webRTC session ${webrtcSessionId} not found.`)
    return
  }

  const { id: callId } = yield select(getCallByWebrtcSessionId, webrtcSessionId)

  /*
   * Run the remote SDP offer through any SDP handlers provided, then set it
   *    as the Session's remote description.
   * This is the "pre set remote" stage.
   */
  const callConfigOptions = yield select(getOptions)
  offer = yield call(runPipeline, callConfigOptions.sdpHandlers, offer, {
    callId,
    type: 'offer',
    step: 'set',
    endpoint: 'remote'
  })

  let answer
  try {
    // TODO: Make sure the session is in the correct signaling state to process an offer.
    const error = yield call([session, 'processOffer'], {
      type: 'offer',
      sdp: offer
    })

    if (error) {
      return { error }
    }

    /*
     * Create the local SDP answer, run it through any provided SDP handlers,
     *    then set it as the Session's local description.
     */
    answer = yield call([session, 'createAnswer'])

    // This is the "pre set local" stage.
    answer.sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, answer.sdp, {
      callId,
      type: answer.type,
      step: 'set',
      endpoint: 'local',
      bandwidth
    })

    answer = yield call([session, 'setLocalDescription'], answer)
  } catch (e) {
    return {
      error: new BasicError({
        message: e.message,
        code: callCodes.GENERIC_ERROR
      }),
      answerSDP: null
    }
  }

  // Run the SDP through the Pipeline again before we send it to the remote side.
  //    This is the "pre send local" stage.
  // Assign it to a new variable because some browsers enforce the read-only
  //    properties of a RTCSessionDescription object. The object from
  //    setLocalDescription is enforced read-only, but the `offer` before that
  //    is not enforced.
  const newSdp = yield call(runPipeline, callConfigOptions.sdpHandlers, answer.sdp, {
    callId,
    type: answer.type,
    step: 'send',
    endpoint: 'local',
    bandwidth
  })

  return {
    answerSDP: newSdp
  }
}

/**
 * Performs the webRTC session functions associated with creating an SDP offer
 *
 * @method generateOffer
 * @param {Object} deps
 * @param {Object} deps.webRTC      The WebRTC stack.
 * @param {string} sessionId the local webRTC session id, used to lookup the session object
 * @param {Object} mediaDirections
 * @param {string} mediaDirections.audio mode of audio add to the sdp offer
 * @param {string} mediaDirections.video mode of video add to the sdp offer
 * @param {BandwidthControls} bandwidth bandwidth configurations to use
 * @return {Object} offer object containing a Session Description Protocol
 */
export function * generateOffer (deps, sessionId, mediaDirections, bandwidth) {
  const { webRTC } = deps
  let session
  try {
    session = yield call([webRTC.sessionManager, 'get'], sessionId)
  } catch (err) {
    /*
     * A WebRTC operation should only throw in Proxy mode when the channel times-out
     *    the operation. Check the first "get" in the saga to catch this problem.
     */
    log.warn(err.message)
    return
  }

  if (!session) {
    log.debug(`webRTC session ${sessionId} not found.`)
    return
  }

  const { id: callId } = yield select(getCallByWebrtcSessionId, sessionId)

  /*
   * Create the local SDP offer, run it through any provided SDP handlers,
   *    then set it as the Session's local description.
   */
  // TODO: Make sure the session is in the correct signaling state to start a
  //    renegotiation operation.
  let offer, callConfigOptions

  try {
    offer = yield call([session, 'createOffer'], {
      mediaDirections
    })
    callConfigOptions = yield select(getOptions)
    // This is the "pre set local" stage.
    offer.sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
      callId,
      type: offer.type,
      step: 'set',
      endpoint: 'local',
      bandwidth
    })

    offer = yield call([session, 'setLocalDescription'], offer)
  } catch (e) {
    return {
      error: new BasicError({
        message: e.message,
        code: callCodes.GENERIC_ERROR
      }),
      type: null,
      sdp: null
    }
  }
  // This is the "pre send local" stage.
  // Assign it to a new variable because some browsers enforce the read-only
  //    properties of a RTCSessionDescription object. The object from
  //    setLocalDescription is enforced read-only, but the `offer` before that
  //    is not enforced.
  const newSdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
    callId,
    type: offer.type,
    step: 'send',
    endpoint: 'local',
    bandwidth
  })

  return { type: offer.type, sdp: newSdp }
}

/**
 * Performs the webRTC responsibilities for adding media
 *
 * @param {Object} deps
 * @param {Object} deps.webRTC      The WebRTC stack.
 * @param  {Object} mediaConstraints Video and audio media constraints
 * @param  {boolean} mediaConstraints.audio Whether to enable audio or not
 * @param  {boolean} mediaConstraints.video Whether to enable video or not
 * @param  {Object} sessionOptions
 * @param  {string} sessionOptions.sessionId the local webrtc session id
 * @param  {Object} [sessionOptions.bandwidth] Contains configuration details for setting bandwidth
 * @return {Object} Object
 * @return {string} Object.sdp An offer in the form of a Session Description Protocol
 * @return {Object} Object.media media object containing tracks
 */
export function * webRtcAddMedia (deps, mediaConstraints, sessionOptions) {
  const { webRTC } = deps
  const { medias, error } = yield call(mediaOps.createLocal, webRTC, mediaConstraints)
  const { sessionId, bandwidth, dscpControls } = sessionOptions

  if (error) {
    return { error }
  }
  const session = yield call([webRTC.sessionManager, 'get'], sessionId)
  const { id: callId } = yield select(getCallByWebrtcSessionId, sessionId)

  let screenTracks = []
  let audioTracks = []
  let videoTracks = []
  let allTracks = []

  for (const eachMedia of medias) {
    const tracks = yield call([eachMedia.media, 'getTracks'])
    if (eachMedia.type === 'screen') {
      screenTracks = [...screenTracks, ...tracks]
    } else if (eachMedia.type === 'audio') {
      audioTracks = [...audioTracks, ...tracks]
    } else if (eachMedia.type === 'video') {
      videoTracks = [...videoTracks, ...tracks]
    }
    allTracks = [...allTracks, ...tracks]
  }

  const dscpTrackMapping = getTrackDscpMapping(audioTracks, videoTracks, screenTracks, dscpControls)

  yield call([session, 'addTracks'], allTracks, dscpTrackMapping)

  /*
   * Create the local SDP offer, run it through any provided SDP handlers,
   *    then set it as the Session's local description.
   */
  // TODO: Make sure the session is in the correct signaling state to start a
  //    renegotiation operation.
  let offer, callConfigOptions
  try {
    offer = yield call([session, 'createOffer'])
    callConfigOptions = yield select(getOptions)
    // This is the "pre set local" stage.
    offer.sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
      callId,
      type: offer.type,
      step: 'set',
      endpoint: 'local',
      bandwidth
    })

    offer = yield call([session, 'setLocalDescription'], offer)
  } catch (e) {
    return {
      error: new BasicError({
        message: e.message,
        code: callCodes.GENERIC_ERROR
      }),
      medias: null,
      sdp: null
    }
  }

  // This is the "pre send local" stage.
  // Assign it to a new variable because some browsers enforce the read-only
  //    properties of a RTCSessionDescription object. The object from
  //    setLocalDescription is enforced read-only, but the `offer` before that
  //    is not enforced.
  const newSdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
    callId,
    type: offer.type,
    step: 'send',
    endpoint: 'local',
    bandwidth
  })

  let mediaStates = []

  for (const eachMedia of medias) {
    const mediaState = yield call([eachMedia.media, 'getState'])

    mediaStates = [...mediaStates, mediaState]
  }

  return { error: false, medias: mediaStates, sdp: newSdp }
}

/**
 * Performs the webRTC responsibilities for removing media
 *
 * @param {Object} deps
 * @param {Object} deps.webRTC      The WebRTC stack.
 * @param {Object} sessionOptions
 * @param  {string} sessionOptions.sessionId The local webrtc session id
 * @param {Array} sessionOptions.tracks A list of track IDs to remove
 * @param  {Object} [sessionOptions.bandwidth] Contains configuration details for setting bandwidth
 * @return {Object} Object
 * @return {string} Object.sdp An offer in the form of a Session Description Protocol
 */
export function * webRtcRemoveMedia (deps, sessionOptions) {
  const { webRTC } = deps
  const { sessionId, tracks, bandwidth } = sessionOptions

  // Get the tracks that we want to remove
  let localTracksToRemove
  try {
    localTracksToRemove = yield call([webRTC.track, 'getTracks'], tracks)
  } catch (err) {
    /*
     * A WebRTC operation should only throw in Proxy mode when the channel times-out
     *    the operation. Check the first "get" in the saga to catch this problem.
     */
    log.info(err.message)
    return { error: err }
  }

  // Get the indexes of undefined tracks.
  const invalidIndexes = localTracksToRemove.reduce((acc, cur, ind) => {
    return isUndefined(cur) ? acc.concat(ind) : acc
  }, [])
  // Get the track IDs of those indexes.
  const invalidTracks = invalidIndexes.map(ind => tracks[ind])

  if (!isEmpty(invalidTracks)) {
    const message = `The following invalid track ids were provided: ${invalidTracks.join(', ')}`
    log.debug(message)
    return {
      error: new BasicError({
        code: callCodes.INVALID_PARAM,
        message
      })
    }
  }

  const session = yield call([webRTC.sessionManager, 'get'], sessionId)
  const { id: callId } = yield select(getCallByWebrtcSessionId, sessionId)

  // Removes tracks from peer (Will stop tracks from being sent to remote participant).
  // Does NOT end the tracks.
  yield call([session, 'removeTracks'], tracks)

  // TODO: This should only cleanup the removed tracks, shouldn't it?
  // Ends the tracks.
  // Clean-up the local tracks.
  yield all(localTracksToRemove.map(track => call([track, 'stop'])))

  /*
   * Create the local SDP offer, run it through any provided SDP handlers,
   *    then set it as the Session's local description.
   */
  // TODO: Make sure the session is in the correct signaling state to start a
  //    renegotiation operation.
  let offer, callConfigOptions
  try {
    offer = yield call([session, 'createOffer'])
    callConfigOptions = yield select(getOptions)
    // This is the "pre set local" stage.
    offer.sdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
      callId,
      type: offer.type,
      step: 'set',
      endpoint: 'local',
      bandwidth
    })

    offer = yield call([session, 'setLocalDescription'], offer)
  } catch (e) {
    return {
      error: new BasicError({
        message: e.message,
        code: callCodes.GENERIC_ERROR
      }),
      sdp: null
    }
  }
  // This is the "pre send local" stage.
  // Assign it to a new variable because some browsers enforce the read-only
  //    properties of a RTCSessionDescription object. The object from
  //    setLocalDescription is enforced read-only, but the `offer` before that
  //    is not enforced.
  const newSdp = yield call(runPipeline, callConfigOptions.sdpHandlers, offer.sdp, {
    callId,
    type: offer.type,
    step: 'send',
    endpoint: 'local',
    bandwidth
  })

  return {
    sdp: newSdp
  }
}

/**
 * Performs the webRTC responsibilities for replacing a track
 *
 * @param {Object} webRTC A WebRTC instance
 * @param {Object} params
 * @param {string} params.sessionId The id of the session to replace the track of
 * @param {string} params.trackId The id of the track to replace
 * @param  {Object} params.mediaConstraints Video or audio media constraints
 * @param  {Object|boolean} params.mediaConstraints.audio Audio configs
 * @param  {Object|boolean} params.mediaConstraints.video Video configs
 * @param  {Object|boolean} params.mediaConstraints.screenShare Screen sharing configs
 * @returns {Object} replaceTrackResult The result of the track replace operation
 * @returns {BasicError} [replaceTrackResult.error] Error object
 * @returns {string} newTrackId The ID of the new track that we used to replace the old one.
 * @returns {Object} replaceTrackResult.oldTrackState The state of the old track before it was replaced.
 */
export function * webRtcReplaceTrack (webRTC, params) {
  const { sessionId, trackId, mediaConstraints } = params

  let noOfTruthyTrackTypes = 0
  let newTrackKind
  Object.entries(mediaConstraints).forEach(([key, value]) => {
    if (value) {
      noOfTruthyTrackTypes++
      newTrackKind = key
    }
  })

  // Need to handle both cases as valid media constraints, for example:
  //   mediaConstraints = {video: true, audio: false, screenShare: false}
  //   mediaConstraints = {video: true}
  if (noOfTruthyTrackTypes !== 1) {
    // We require exactly one new track to be provided by API.
    return {
      error: new BasicError({
        code: callCodes.INVALID_PARAM,
        message: `Require exactly one new track type to be specified. Found ${noOfTruthyTrackTypes} track types.`
      }),
      newTrackId: undefined
    }
  }

  let session
  try {
    session = yield call([webRTC.sessionManager, 'get'], sessionId)
  } catch (err) {
    /*
     * A WebRTC operation should only throw in Proxy mode when the channel times-out
     *    the operation. Check the first "get" in the saga to catch this problem.
     */
    log.debug(err.message)
    return { error: err }
  }
  const oldTrack = session.allLocalTracks.find(track => track.id === trackId)

  if (!oldTrack) {
    return {
      error: new BasicError({
        code: callCodes.INVALID_PARAM,
        message: `Old track ${trackId} not found.`
      }),
      newTrackId: undefined
    }
  }

  const oldTrackState = yield call([oldTrack, 'getState'])
  const oldTrackKind = oldTrackState.kind

  // At the moment, only video & screen share are compatible tracks that can replace each other.
  // (That's because screen share is sent as a video track to remote peer)
  // Otherwise, the exact media type is required in order to allow a track replacement.
  const compatibleTrackKinds =
    (oldTrackKind === 'audio' && newTrackKind === 'audio') ||
    (oldTrackKind === 'video' && ['video', 'screenShare'].includes(newTrackKind))

  if (!compatibleTrackKinds) {
    return {
      error: new BasicError({
        code: callCodes.INVALID_PARAM,
        message: `Media constraints incompatible for old track kind: ${oldTrackKind}.`
      }),
      newTrackId: undefined
    }
  }

  // Create Media
  const { medias: newMedias, error: createLocalError } = yield call(mediaOps.createLocal, webRTC, mediaConstraints)
  if (createLocalError) {
    return { error: createLocalError, newTrackId: undefined }
  }

  let allNewTracks = []

  // Find the new track that matches the old track we want to replace.
  for (const eachMedia of newMedias) {
    const newTracks = yield call([eachMedia.media, 'getTracks'])
    allNewTracks = [...allNewTracks, ...newTracks]
  }

  let newTrack
  for (const track of allNewTracks) {
    // Matching the track is done this way because we need to yield to getState,
    //    but we can't yield inside of a `.find(func)` or similar. So iterate
    //    over the new tracks "manually".
    const state = yield call([track, 'getState'])
    if (state.kind === oldTrackKind) {
      newTrack = track
      break
    }
  }

  if (!newTrack) {
    return {
      error: new BasicError({
        code: callCodes.USER_MEDIA_ERROR,
        message: `${oldTrackKind} track not found`
      }),
      newTrackId: undefined
    }
  }

  // Replaces the track
  const replaceTrackError = yield call([session, 'replaceTrack'], newTrack, { trackId })
  if (replaceTrackError) {
    // If cannot replace old track, cleanup the newly created track
    yield call([newTrack, 'stop'])
    return {
      error: new BasicError({
        code: callCodes.GENERIC_ERROR,
        message: replaceTrackError.message
      }),
      newTrackId: undefined
    }
  }

  // stop the old track
  yield call([oldTrack, 'stop'])

  return {
    newTrackId: newTrack.id,
    oldTrackState
  }
}
